import React, { useEffect, useState, useCallback } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchUser, logoutUser } from "../../store/common/authSlice";
import {
    fetchImpersonationStatus,
    stopImpersonation,
} from "../../store/superAdmin/impersonationSlice";
import IntroModal from "../utilities/IntroModal";
import { CiLock } from "react-icons/ci";
import {
    SimpleField,
    DropdownField,
    superAdminFields,
    adminReadFields,
    sideAdminFields,
    userFields,
} from "../../types/SidebarTypes/smileSidebar";
import { getSmileSidebarIcon, SmileLogoutIcon } from "./smileSidebarIcons";

function isLinkActive(pathname: string, link: string) {
    if (pathname === link) return true;
    if (link.endsWith("/generate")) {
        return pathname.startsWith("/dashboard/smiledesign/generate");
    }
    return pathname.startsWith(`${link}/`);
}

interface RailItemProps {
    to: string;
    label: string;
    icon: React.ReactNode;
    isActive: boolean;
    locked?: boolean;
    variant?: "default" | "logout";
    onClick?: (e: React.MouseEvent) => void;
    badgeCount?: number;
}

function RailItem({ to, label, icon, isActive, locked, variant = "default", onClick, badgeCount }: RailItemProps) {
    const isLogout = variant === "logout";

    const content = (
        <>
            <span
                className={`flex items-center justify-center w-12 h-12 rounded-[11px] transition-all duration-150 ${
                    isActive
                        ? "bg-[rgba(29,168,198,0.14)] text-[#1DA8C6]"
                        : isLogout
                          ? "text-[#E8503A] group-hover:bg-[rgba(232,80,58,0.14)] group-hover:text-[#E8503A]"
                          : "text-[#6B6C72] group-hover:bg-[#26282C] group-hover:text-[#F1F0EA]"
                }`}
            >
                {icon}
            </span>
            {isActive && (
                <span
                    aria-hidden
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-sm bg-[#1DA8C6]"
                />
            )}
            <span className="pointer-events-none absolute left-[3.75rem] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-[7px] border border-white/[0.14] bg-[#0D0E10] px-2.5 py-1.5 text-xs text-[#F1F0EA] opacity-0 shadow-[0_8px_20px_rgba(0,0,0,0.4)] transition-all duration-150 group-hover:opacity-100 group-hover:translate-x-1 z-50">
                {label}
            </span>
            {badgeCount !== undefined && badgeCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                    {badgeCount}
                </span>
            )}
            {locked && (
                <CiLock className="absolute bottom-0.5 right-0.5 w-3 h-3 text-amber-400" />
            )}
        </>
    );

    const className =
        "group relative flex items-center justify-center w-full py-0.5";

    if (onClick) {
        return (
            <button type="button" onClick={onClick} title={label} className={className}>
                {content}
            </button>
        );
    }

    return (
        <Link to={to} title={label} className={className} onClick={onClick}>
            {content}
        </Link>
    );
}

const Sidebar: React.FC = () => {
    const { user } = useAppSelector((state) => state.auth);
    const { isImpersonating } = useAppSelector((state) => state.impersonation);
    const role = user?.role ?? "";
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const [isOpen, setIsOpen] = useState(false);
    const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});
    const [showIntroModal, setShowIntroModal] = useState(false);

    const isUserProfileIncomplete =
        role === "User" && !user?.verified && !user?.verifySmile;
    const currentPath = location.pathname;

    const toggleSidebar = () => setIsOpen((prev) => !prev);
    const closeSidebar = () => setIsOpen(false);

    const toggleDropdown = (fieldName: string) => {
        setOpenDropdowns((prev) => ({
            ...prev,
            [fieldName]: !prev[fieldName],
        }));
    };

    const handleLockClick = () => {
        setShowIntroModal(true);
        closeSidebar();
        if (window.customNavigate) {
            window.customNavigate("/dashboard/smiledesign/profile");
        } else {
            navigate("/dashboard/smiledesign/profile");
        }
    };

    const fetchImpersonationStatusCallback = useCallback(() => {
        dispatch(fetchImpersonationStatus());
    }, [dispatch]);

    useEffect(() => {
        fetchImpersonationStatusCallback();
    }, [fetchImpersonationStatusCallback]);

    useEffect(() => {
        dispatch(fetchUser());
    }, [dispatch]);

    useEffect(() => {
        if (!isOpen) return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, [isOpen]);

    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth >= 768) setIsOpen(false);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const handleLogout = async () => {
        closeSidebar();
        const navigateAway = (path: string) => {
            if (window.customNavigate) {
                window.customNavigate(path);
            } else {
                navigate(path);
            }
        };

        if (isImpersonating) {
            try {
                await dispatch(stopImpersonation()).unwrap();
                // Hard refresh so session restores as Super Admin
                window.location.href = "/dashboard/profile";
            } catch (error) {
                console.error("Error during impersonation stop or user fetch:", error);
            }
        } else {
            dispatch(logoutUser());
            navigateAway("/");
        }
    };

    if (
        ![
            "Super Admin",
            "Side Admin",
            "Admin Read",
            "Admin",
            "Operations",
            "Production",
            "User",
        ].includes(role)
    )
        return null;

    let sidebarFields: (SimpleField | DropdownField)[] = [];

    switch (role) {
        case "Super Admin":
            sidebarFields = superAdminFields;
            break;
        case "Side Admin":
            sidebarFields = sideAdminFields;
            break;
        case "Admin Read":
            sidebarFields = adminReadFields;
            break;
        case "User":
            sidebarFields = userFields;
            break;
        default:
            sidebarFields = [];
    }

    const isSuperAdmin = role === "Super Admin";
    const hasCasesAccess = !!user?.verified || isSuperAdmin;
    const hasSmileAccess = !!user?.verifySmile || isSuperAdmin;

    if (hasCasesAccess && hasSmileAccess) {
        sidebarFields = sidebarFields.filter((field) => field.fieldName !== "Profile");
    }

    const handleMobileNavClick = () => {
        if (window.innerWidth < 768) closeSidebar();
    };

    const renderNavItem = (field: SimpleField | DropdownField, mobile = false) => {
        if (field.isSubField) {
            if (mobile) {
                return (
                    <div key={field.fieldName} className="w-full">
                        <button
                            type="button"
                            onClick={() => toggleDropdown(field.fieldName)}
                            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm ${
                                openDropdowns[field.fieldName]
                                    ? "bg-[rgba(29,168,198,0.14)] text-[#1DA8C6]"
                                    : "text-[#A5A6AC] hover:bg-[#26282C] hover:text-[#F1F0EA]"
                            }`}
                        >
                            {getSmileSidebarIcon(field.fieldName)}
                            <span>{field.fieldName}</span>
                        </button>
                        {openDropdowns[field.fieldName] && (
                            <div className="ml-4 mt-1 flex flex-col gap-1">
                                {(field as DropdownField).subFieldList.map((sub) => (
                                    <Link
                                        key={sub.subFieldName}
                                        to={sub.subFieldLink}
                                        onClick={handleMobileNavClick}
                                        className={`rounded-lg px-3 py-2 text-sm ${
                                            currentPath === sub.subFieldLink
                                                ? "text-[#1DA8C6] font-medium"
                                                : "text-[#A5A6AC] hover:text-[#F1F0EA]"
                                        }`}
                                    >
                                        {sub.subFieldName}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                );
            }
            return null;
        }

        const simple = field as SimpleField;
        const locked =
            role === "User" &&
            isUserProfileIncomplete &&
            field.fieldName !== "Profile";
        const isActive = isLinkActive(currentPath, simple.fieldLink);

        if (mobile) {
            return (
                <Link
                    key={field.fieldName}
                    to={locked ? "#" : simple.fieldLink}
                    onClick={(e) => {
                        if (locked) {
                            e.preventDefault();
                            handleLockClick();
                        } else {
                            handleMobileNavClick();
                        }
                    }}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                        isActive
                            ? "bg-[rgba(29,168,198,0.14)] text-[#1DA8C6] font-medium"
                            : "text-[#A5A6AC] hover:bg-[#26282C] hover:text-[#F1F0EA]"
                    }`}
                >
                    <span
                        className={`flex h-9 w-9 items-center justify-center rounded-[10px] ${
                            isActive ? "text-[#1DA8C6]" : "text-[#6B6C72]"
                        }`}
                    >
                        {getSmileSidebarIcon(field.fieldName)}
                    </span>
                    <span className="flex-1">{field.fieldName}</span>
                    {locked && <CiLock className="w-4 h-4 text-amber-400" />}
                    {field.isBadge && simple.badgeCount !== undefined && simple.badgeCount > 0 && (
                        <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                            {simple.badgeCount}
                        </span>
                    )}
                </Link>
            );
        }

        return (
            <RailItem
                key={field.fieldName}
                to={locked ? "#" : simple.fieldLink}
                label={field.fieldName}
                icon={getSmileSidebarIcon(field.fieldName)}
                isActive={isActive}
                locked={locked}
                badgeCount={field.isBadge ? simple.badgeCount : undefined}
                onClick={
                    locked
                        ? (e) => {
                              e.preventDefault();
                              handleLockClick();
                          }
                        : undefined
                }
            />
        );
    };

    return (
        <>
            <button
                type="button"
                onClick={toggleSidebar}
                aria-label={isOpen ? "Close menu" : "Open menu"}
                aria-expanded={isOpen}
                className="md:hidden fixed top-[4.25rem] left-3 z-[60] flex h-10 w-10 items-center justify-center rounded-xl bg-[#212226] text-[#F1F0EA] border border-white/[0.07] shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
            >
                {isOpen ? <FaTimes size={16} /> : <FaBars size={16} />}
            </button>

            {/* Desktop icon rail */}
            <aside className="portal-sidebar hidden md:flex flex-col items-center w-[96px] h-full py-4 gap-1.5 border-r border-white/[0.07] bg-[#1C1D20] shrink-0">
                {sidebarFields.map((field) => renderNavItem(field, false))}

                <div className="flex-1 min-h-4" />

                <RailItem
                    to="#"
                    label="Logout"
                    icon={<SmileLogoutIcon />}
                    isActive={false}
                    variant="logout"
                    onClick={(e) => {
                        e.preventDefault();
                        handleLogout();
                    }}
                />

                {isImpersonating && (
                    <div className="mx-2 mt-2 rounded-lg border border-red-500/50 p-2 text-xs text-red-400 text-center leading-tight">
                        Impersonating
                    </div>
                )}
            </aside>

            {/* Mobile drawer */}
            <div
                className={`portal-sidebar smile-dark-scroll fixed top-0 left-0 h-full w-[min(80%,18rem)] bg-[#1C1D20] text-[#F1F0EA] z-[70] transform flex flex-col overflow-y-auto md:hidden
                    ${isOpen ? "translate-x-0" : "-translate-x-full"}
                    transition-transform duration-300 ease-in-out`}
            >
                <nav className="flex w-full flex-col gap-1 mt-20 px-3 pb-8">
                    {sidebarFields.map((field) => renderNavItem(field, true))}

                    <button
                        type="button"
                        onClick={handleLogout}
                        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-[#E8503A] hover:bg-[rgba(232,80,58,0.14)] transition-colors mt-2"
                    >
                        <span className="flex h-9 w-9 items-center justify-center rounded-[10px]">
                            <SmileLogoutIcon />
                        </span>
                        Logout
                    </button>

                    {isImpersonating && (
                        <div className="mt-4 rounded-xl border border-red-500/40 p-3 text-xs text-red-400">
                            <p>Impersonating as</p>
                            <p className="font-medium text-[#F1F0EA]">{user?.user_name}</p>
                            <p>{user?.token}</p>
                        </div>
                    )}
                </nav>
            </div>

            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/60 z-[65] md:hidden backdrop-blur-sm"
                    onClick={closeSidebar}
                    aria-hidden="true"
                />
            )}

            {showIntroModal && (
                <IntroModal onClose={() => setShowIntroModal(false)} />
            )}
        </>
    );
};

export default Sidebar;
